/**
 * OR-6504: the Definition body of a variable GROUP's details page.
 *
 * Mirrors a single variable's Definition tab section-for-section — Alias,
 * Description, Output, Concept mappings — because the group IS a variable
 * definition; what differs is that one field is a parameter rather than a value.
 * Keeping the same shape and order is the point: a researcher who has read a
 * variable page can read this one without learning a second layout.
 *
 * Three group-specific departures, each forced by there being N variables and
 * (until approval) no drafts at all:
 *
 * - **Alias** shows the recovered pattern (`baseline_{diagnosis}`), which is what
 *   every member's alias instantiates. Absent when the pattern was suppressed —
 *   the aliases genuinely stopped sharing a shape — in which case there is
 *   nothing truthful to show and the row is dropped.
 * - **Description** carries the shared definition, then names the values the
 *   members will be built from as a NUMBERED list. The numbering is
 *   load-bearing: the mapping table's "Used in" column refers to these
 *   positions.
 * - **Concept mappings** are pooled — each term appears once, with the member
 *   positions that reference it — instead of repeating a near-identical list N
 *   times. The two parameter kinds fill that column inversely: a concept-group
 *   family gives each varying term one member, while a window family's single
 *   term is referenced by all of them.
 *
 * The parameter appears as a token pill wherever it occurs (in the alias
 * pattern, in the prose, in the "is one of" line), so the one thing that varies
 * is visually obvious in a page of otherwise fixed text.
 *
 * Presentational and read-only.
 */

import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { ConceptMappingsTable } from './artifact-tabs/ConceptMappingsTable';
import type { ConceptMapping, EpiVariableFamily } from '../types/epi-chat-types';

export interface VariableFamilyDefinitionProps {
  family: EpiVariableFamily;
}

/** Section heading, matching the variable page's Definition tab. */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
    {children}
  </Typography>
);

/**
 * A `{parameter}` token, rendered as a pill so the varying part stands out from
 * the fixed text around it.
 */
const ParameterToken = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="code"
    sx={{
      paddingX: 1,
      paddingY: 0.25,
      borderRadius: `${borderRadius.xs}px`,
      backgroundColor: 'action.hover',
      fontFamily: 'monospace',
      fontSize: '0.875em',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

/**
 * The token the "is one of" line should use: the one the DESCRIPTION itself uses.
 *
 * The model names the parameter twice — once as `parameter_label` (which the alias
 * pattern is built from) and once inline in the generalized description — and the
 * two can differ: observed live as an alias pattern reading
 * `confirmed_copd_dx_{follow-up window}` above a description reading "within
 * {window} following the index date". Building the value list from the label then
 * introduced a third name into the same paragraph, so a reader saw the parameter
 * called `{window}` in one sentence and `{follow-up window}` in the next.
 *
 * Product's rule: the list of values uses the label the DESCRIPTION uses, not the
 * one the alias uses. So take the first token that occurs in the description, and
 * fall back to the label only when the description names no token at all.
 */
const descriptionParameterToken = (family: EpiVariableFamily): string => {
  const inDescription = family.generalizedDescription.match(/\{[^{}]+\}/);
  return inDescription ? inDescription[0] : `{${family.parameterLabel || 'parameter'}}`;
};

/**
 * How a member's value reads in the "is one of" list.
 *
 * Built from the window's STRUCTURE (value, unit, direction) rather than the
 * model's prose, so the list reads consistently and states the side of the index
 * date exactly once. Deriving it was not the first attempt: appending "before the
 * index date" to the model's own wording produced "365-day baseline window before
 * the index date", because the model already encodes the direction in the value
 * it writes ("baseline", "follow-up", "post-index").
 *
 * Deliberately stops at the side, and does not spell out day boundaries. The
 * drafter's own predicates include the index day (`event_date >= idx.value`) while
 * the descriptions say "following the index date", so a page claiming "day 1
 * through day 90" would be precisely wrong and one claiming "day 0 through day 90"
 * would contradict its own sentence. Naming the side is strictly more informative
 * and cannot be either.
 *
 * Falls back to the model's wording when there is no window with a declared
 * direction — a concept-group family, or a spec proposed before the field existed.
 */
const memberValueLabel = (
  member: EpiVariableFamily['members'][number],
  intl: ReturnType<typeof useIntl>
): string => {
  const window = member.window;
  const direction = window?.direction;
  if (!window || (direction !== 'after' && direction !== 'before')) return member.parameterValue;
  return intl.formatMessage(
    {
      id:
        direction === 'after'
          ? 'epiChat.variableFamilyPage.windowAfterIndex'
          : 'epiChat.variableFamilyPage.windowBeforeIndex',
    },
    { value: window.value, unit: window.unit }
  );
};

/**
 * Split text on `{…}` tokens and render each as a {@link ParameterToken}.
 *
 * The generalized description names the parameter inline ("…a diagnosis of
 * {diagnosis} in the 12 months prior…"), and the alias pattern is the same shape
 * (`baseline_{diagnosis}`), so both render through this. Text with no token
 * passes through unchanged.
 */
const withTokenPills = (text: string): React.ReactNode[] =>
  text
    .split(/(\{[^{}]*\})/g)
    .map((piece, index) =>
      piece.startsWith('{') && piece.endsWith('}') ? (
        <ParameterToken key={`${index}-${piece}`}>{piece}</ParameterToken>
      ) : (
        piece
      )
    );

/**
 * The Values half of the Output section, resolved the way a single variable's
 * Description tab resolves it (OR-6266): a boolean or date output has
 * deterministic values, so they are stated rather than left blank when the model
 * did not author an ``output_values`` string. Reuses the same message ids, so the
 * group page and the variable page cannot drift on what "boolean" means.
 */
const resolveOutputValues = (
  family: EpiVariableFamily,
  intl: ReturnType<typeof useIntl>
): string | undefined => {
  const format = (family.outputType || '').trim().toLowerCase();
  if (format === 'boolean')
    return intl.formatMessage({ id: 'epiChat.descriptionTab.values.boolean' });
  if (format === 'date') return intl.formatMessage({ id: 'epiChat.descriptionTab.values.date' });
  return family.outputValues?.trim() || undefined;
};

export const VariableFamilyDefinition = ({ family }: VariableFamilyDefinitionProps) => {
  const intl = useIntl();
  const parameterToken = descriptionParameterToken(family);
  const outputValues = resolveOutputValues(family, intl);
  // Reshaped for the SHARED mappings table, so the group page and the variable
  // page render through one component and cannot drift on chips, click-through or
  // layout. Keyed on the clinical term, which is unique within a pool by
  // construction (each term is mapped once — that is what "shared" means here).
  const mappings: ConceptMapping[] = useMemo(
    () =>
      family.conceptMappings.map((row) => ({
        mappingId: row.clinicalTerm,
        clinicalTerm: row.clinicalTerm,
        table: null,
        column: null,
        domain: null,
        type: 'concept_group' as const,
        // ``owner`` is absent from the family payload, which makes the chip
        // resolve itself by id — the same path a variable manifest without an
        // owner takes, so the icon still renders.
        conceptGroups: row.conceptGroups.map((group) => ({
          conceptGroupId: group.conceptGroupId,
          name: group.name,
          owner: null,
          domains: [],
        })),
        inlineConcepts: [],
        status: null,
      })),
    [family.conceptMappings]
  );
  const appliedTo = useMemo(
    () => new Map(family.conceptMappings.map((row) => [row.clinicalTerm, row.usedIn])),
    [family.conceptMappings]
  );

  return (
    <Box
      data-testid="variable-family-definition"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        padding: 5,
        margin: 4,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: `${borderRadius.sm}px`,
      }}
    >
      {family.aliasPattern && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <SectionLabel>
            {intl.formatMessage({
              id: 'epiChat.variableFamilyPage.aliasLabel',
              defaultMessage: 'Alias',
            })}
          </SectionLabel>
          <Typography
            variant="body2"
            data-testid="variable-family-definition-alias"
            sx={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {withTokenPills(family.aliasPattern)}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <SectionLabel>
          {intl.formatMessage({
            id: 'epiChat.variableFamilyPage.descriptionLabel',
            defaultMessage: 'Description',
          })}
        </SectionLabel>
        <Typography variant="body2" data-testid="variable-family-definition-description">
          {withTokenPills(family.generalizedDescription)}
        </Typography>
        <Typography variant="body2" sx={{ marginTop: 2 }}>
          {intl.formatMessage(
            {
              id: 'epiChat.variableFamilyPage.valuesIntro',
              defaultMessage: 'Individual variables will be created where {token} is one of:',
            },
            { token: <ParameterToken key="token">{parameterToken}</ParameterToken> }
          )}
        </Typography>
        <Box
          component="ol"
          data-testid="variable-family-definition-values"
          sx={{
            margin: 0,
            paddingLeft: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            color: 'text.secondary',
          }}
        >
          {family.members.map((member) => (
            <Box component="li" key={member.alias || member.parameterValue}>
              <Typography variant="body2" component="span" sx={{ color: 'text.primary' }}>
                {memberValueLabel(member, intl)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {family.outputType && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <SectionLabel>
            {intl.formatMessage({
              id: 'epiChat.variableFamilyPage.outputLabel',
              defaultMessage: 'Output',
            })}
          </SectionLabel>
          <Stack direction="row" data-testid="variable-family-definition-output">
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                {intl.formatMessage({
                  id: 'epiChat.variableFamilyPage.outputFormat',
                  defaultMessage: 'Format',
                })}
              </Typography>
              <Typography variant="body2">{family.outputType}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              {outputValues && (
                <>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {intl.formatMessage({
                      id: 'epiChat.variableFamilyPage.outputValues',
                      defaultMessage: 'Values',
                    })}
                  </Typography>
                  <Typography
                    variant="body2"
                    data-testid="variable-family-definition-output-values"
                  >
                    {outputValues}
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      {mappings.length > 0 && (
        <ConceptMappingsTable
          mappings={mappings}
          caption={intl.formatMessage({
            id: 'epiChat.variableFamilyPage.conceptMappingsCaption',
            defaultMessage:
              'Shared across the group — each term is mapped once. "Applied to" shows which of the numbered variables above reference it.',
          })}
          extraColumn={{
            header: intl.formatMessage({
              id: 'epiChat.variableFamilyPage.mappingAppliedTo',
              defaultMessage: 'Applied to',
            }),
            render: (mapping) => (
              // Positions are enumerated even for a term every member shares:
              // the reader is checking one variable's coverage, and "all" makes
              // them count the list themselves to confirm theirs is in it.
              <Stack direction="row" spacing={2} component="span">
                {(appliedTo.get(mapping.mappingId) ?? []).map((position) => (
                  <Typography key={position} variant="body2" component="span">
                    {position}
                  </Typography>
                ))}
              </Stack>
            ),
          }}
        />
      )}
    </Box>
  );
};
